import { serializeReportOriginalFilePaths } from '@home/utils/reportPreview';
import { isEmpty } from '@intelligence/v3/utils/common';
import { getOptionText } from '@intelligence/utils/topchartsUtils';
import { buildReport, handleValidate } from '../../utils';
import { FIELD_COMMON_ERR_MSG } from '../../constants';
import { LOCATION_OPTIONS, DEFAULT_FORM_MODEL, MARKET_REPORT_OTHERS_VALUE } from '../constants';

import type { CascaderOption } from 'tea-component';

interface IMarketReportHelperInfo {
  report_language?: string;
  report_language_en?: string;
  file_cn_language?: string;
  file_en_language?: string;
  creator_name?: string;
  creator_department?: string;
}

interface IBuildMarketReportParams {
  permissionOptions?: CascaderOption[] | null;
  reportHelperInfo?: IMarketReportHelperInfo;
}

export const handleMarketReportValidate = (values?: IReportFormModel | null): IRecord<string> => {
  if (!values) {
    return {};
  }

  const errors = handleValidate({ ...values, file_en: values.file });
  Reflect.deleteProperty(errors, 'creator_name_en');
  Reflect.deleteProperty(errors, 'creator_department_en');
  Reflect.deleteProperty(errors, 'creator_avatar');

  if (isEmpty(values?.file_language)) {
    errors.file_language = FIELD_COMMON_ERR_MSG;
  }
  const location = values?.location || [];
  if (location[1] === MARKET_REPORT_OTHERS_VALUE && isEmpty(location[2])) {
    errors.location = FIELD_COMMON_ERR_MSG;
  }
  return errors;
};

export const getMarketReportLocationValue = (system: string, source: string): string[] => {
  if (!system || !source) {
    return [];
  }

  const directOption = LOCATION_OPTIONS[0]?.children?.find((item) => item?.value === source);
  if (directOption && directOption.value !== MARKET_REPORT_OTHERS_VALUE) {
    return [system, source];
  }

  // 二级没找到、或者二级是 Others时，统一返回 [system, MARKET_REPORT_OTHERS_VALUE, source] 即可
  return [system, MARKET_REPORT_OTHERS_VALUE, source];
};

const getMarketReportSourceValue = (location?: string[]): string => {
  const level2Value = location?.[1] || '';
  if (level2Value === MARKET_REPORT_OTHERS_VALUE) {
    return location?.[2] || '';
  }
  return level2Value;
};

export const getMarketReportSegmentText = (locale: string, source: string): string => {
  if (isEmpty(source)) {
    return '';
  }

  let segmentOption = LOCATION_OPTIONS[0]?.children?.find((item) => {
    return item?.value === source && item?.value !== MARKET_REPORT_OTHERS_VALUE;
  });

  if (isEmpty(segmentOption)) {
    const othersOption = LOCATION_OPTIONS[0]?.children?.find((item) => {
      return item?.value === MARKET_REPORT_OTHERS_VALUE;
    });
    segmentOption = othersOption?.children?.find((item) => item?.value === source);
  }

  return getOptionText(locale, {
    value: segmentOption?.value || source,
    text: segmentOption?.text || '',
    textEN: segmentOption?.text || '',
    textCN: segmentOption?.textCN || '',
  });
};

export const getMarketReportLocationText = (locale: string, system: string, source: string): string => {
  const option = LOCATION_OPTIONS.find((item) => item?.value === system);
  const systemText = getOptionText(locale, {
    value: option?.value || system,
    text: option?.text || '',
    textEN: option?.text || '',
    textCN: option?.textCN || '',
  });
  const segmentText = getMarketReportSegmentText(locale, source);
  return `${systemText}: ${segmentText}`;
};

export const buildMarketReport = (
  report?: IReportFormModel | null,
  params?: IBuildMarketReportParams,
): IReportPartial => {
  const { file_language: fileLanguage, file, location, ...restReport } = report || DEFAULT_FORM_MODEL;
  const reportNew = buildReport(
    {
      ...restReport,
      file,
      file_en: file,
      location,
    },
    params,
  );
  const filePath = file?.key || '';
  const originalFilePath = file?.originalKey || '';

  return {
    ...reportNew,
    system: location?.[0] || '',
    source: getMarketReportSourceValue(location),
    sub_source: '',
    file_cn_language: fileLanguage || '',
    file_en_language: fileLanguage || '',
    file_name: filePath,
    file_name_en: filePath,
    original_file_name: serializeReportOriginalFilePaths({
      filePathCN: originalFilePath,
      filePathEN: originalFilePath,
    }),
  };
};
